import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMachines } from '../context/MachineContext';
import { useWhatsApp } from '../hooks/useWhatsApp';
import MachineCard from '../components/MachineCard';
import HeroSlideshow from '../components/HeroSlideshow';
import { COMPANY } from '../constants';
import styles from './HomePage.module.css';

const STATS = [
  { value: '500+',  label: 'Machines Sold' },
  { value: '20+',   label: 'Years Experience' },
  { value: '100%',  label: 'Verified Stock' },
];

const WHY_US = [
  { icon: '✅', title: 'Verified Listings', desc: 'Every machine is physically verified before listing. What you see is what you get.' },
  { icon: '🔍', title: 'On-Site Inspection', desc: 'Arrange a site visit or inspection at our navi Mumbai Location before purchase.' },
  { icon: '📞', title: 'Responsive Support', desc: 'Reach us by phone or WhatsApp — we respond quickly during business hours.' },
  { icon: '🤝', title: 'Transparent Deals', desc: 'No hidden charges. Clear machine history and pricing on every listing.' },
];

export default function HomePage() {
  const { machines, categories } = useMachines();
  const { openGeneral } = useWhatsApp();
  const navigate = useNavigate();

  const featured = machines.filter(m => m.featured && m.status !== 'Sold').slice(0, 3);
  const available = machines.filter(m => m.status === 'Available').length;

  return (
    <div className={styles.page}>
      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <HeroSlideshow />
        <div className={styles.heroContent}>
          <div className={`${styles.heroBadge} animate-fade-up`}>
            <span className={styles.badgeDot} />
            Used Machine Seller · Navi Mumbai
          </div>
          <h1 className={`${styles.heroTitle} animate-fade-up-1`}>
            <span className={styles.heroTitleYellow}>BRIGHT</span>
            <br />MACHINERIES
          </h1>
          <p className={`${styles.heroSub} animate-fade-up-2`}>
            Quality used metal working machinery — CNC, lathe, milling, grinding &amp; more.
            Physically verified, fairly priced, ready to inspect.
          </p>
          <div className={`${styles.heroActions} animate-fade-up-3`}>
            <Link to="/stock" className={styles.heroPrimary}>
              Browse {available > 0 ? `${available} ` : ''}Available Machines
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </Link>
            <button className={styles.heroWa} onClick={openGeneral}>
              <WaIcon /> WhatsApp Enquiry
            </button>
          </div>
          <div className={`${styles.stats} animate-fade-up-4`}>
            {STATS.map(s => (
              <div key={s.label} className={styles.stat}>
                <div className={styles.statValue}>{s.value}</div>
                <div className={styles.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* ── CATEGORIES ─────────────────────────────────────────────── */}
      <section className={styles.section}>
        <div className="container">
          <SectionHeader
            title="Browse by Category"
            sub="Find the right equipment for your application"
            action={<Link to="/stock" className={styles.seeAll}>View All →</Link>}
          />
          <div className={styles.categoryGrid}>
            {categories.map(cat => {
              const count = machines.filter(m => m.category === cat.id && m.status === 'Available').length;
              return (
                <button
                  key={cat.id}
                  className={styles.catCard}
                  onClick={() => navigate(`/stock?category=${cat.id}`)}
                >
                  <span className={styles.catIcon}>{cat.icon}</span>
                  <span className={styles.catLabel}>{cat.label}</span>
                  <span className={styles.catCount}>{count} available</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURED MACHINES ─────────────────────────────────────────── */}
      {featured.length > 0 && (
        <section className={`${styles.section} ${styles.sectionDark}`}>
          <div className="container">
            <SectionHeader
              title="Our Stock"
              sub="Hand-picked equipment from our verified inventory"
              action={<Link to="/stock" className={styles.seeAll}>See All →</Link>}
            />
            <FeaturedSlideshow machines={featured} />
          </div>
        </section>
      )}

      {/* ── WHY US ─────────────────────────────────────────────────── */}
      <section className={styles.section}>
        <div className="container">
          <SectionHeader title={`Why ${COMPANY.name}?`} sub="Your trusted source for used metal working machinery" />
          <div className={styles.whyGrid}>
            {WHY_US.map(w => (
              <div key={w.title} className={styles.whyCard}>
                <div className={styles.whyIcon}>{w.icon}</div>
                <h3 className={styles.whyTitle}>{w.title}</h3>
                <p className={styles.whyDesc}>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ─────────────────────────────────────────────── */}
      <section className={styles.cta}>
        <div className={styles.ctaStripe} />
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Ready to Buy or Sell Machinery?</h2>
          <p className={styles.ctaSub}>
            Call us or reach out on WhatsApp — we're available Mon–Sat, 9 AM to 6 PM.
          </p>
          <div className={styles.ctaActions}>
            <a href="tel:+917506760967" className={styles.ctaCall}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              Call 7506760967
            </a>
            <button className={styles.ctaWa} onClick={openGeneral}>
              <WaIcon /> WhatsApp Us
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeaturedSlideshow({ machines }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (machines.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % machines.length), 3500);
    return () => clearInterval(t);
  }, [machines.length]);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 }}>
        {/* Show one card at a time on mobile, all on desktop via CSS */}
        {machines.map((m, i) => (
          <div
            key={m.id}
            style={{
              transition: 'opacity 0.5s, transform 0.5s',
              opacity: machines.length === 1 || i === idx ? 1 : 0.35,
              transform: i === idx ? 'scale(1.02)' : 'scale(1)',
            }}
          >
            <MachineCard machine={m} />
          </div>
        ))}
      </div>
      {machines.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {machines.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              style={{
                width: i === idx ? 24 : 8, height: 8,
                borderRadius: 4, border: 'none', cursor: 'pointer',
                background: i === idx ? '#f5c518' : '#3a4a55',
                transition: 'all 0.3s', padding: 0,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, sub, action }) {
  return (
    <div className={styles.sectionHeader}>
      <div>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {sub && <p className={styles.sectionSub}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function WaIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}
